// Adapted from shuding/cobe 04b800b72abfcdc2253309e388477d17015afd71. See LICENSE.
import common from './common.glsl?raw'
import globeVertex from './globe.vert.glsl?raw'
import globeFragment from './globe.frag.glsl?raw'
import pandaVertex from './panda.vert.glsl?raw'
import pandaFragment from './panda.frag.glsl?raw'
import textureURL from './texture'

export type CobePoint = { x: number; y: number; z: number }
export type CobeState = {
  rotation: number
  tilt: number
  radius: number
  ink: [number, number, number]
  green: [number, number, number]
  dark: boolean
  homepage?: boolean
  waveProgress?: number
  waveTarget?: CobePoint | null
  pandaConnected?: boolean
}

/** The host owns the animation clock. update() paints exactly one state. */
export function createGlobe(canvas: HTMLCanvasElement, panda: readonly CobePoint[]) {
  const gl = canvas.getContext('webgl', { alpha: true, depth: false, stencil: false, antialias: true })
  if (!gl) throw new Error('cobe: WebGL is unavailable')
  let lost = false
  let destroyed = false
  let state: CobeState | undefined
  let paintCount = 0
  const image = new Image()
  const extent = Math.max(...panda.map((p) => Math.acos(-p.y)))
  type Program = { handle: WebGLProgram; uniforms: Record<string, WebGLUniformLocation | null>; position: number; point: number }
  let programs: Program[] = []
  let buffers: WebGLBuffer[] = []
  let texture: WebGLTexture | null = null
  let instancing: ANGLE_instanced_arrays

  function program(vertex: string, fragment: string): Program {
    const handle = gl!.createProgram()!
    for (const [type, source] of [
      [gl!.VERTEX_SHADER, vertex],
      [gl!.FRAGMENT_SHADER, fragment],
    ] as const) {
      const shader = gl!.createShader(type)!
      gl!.shaderSource(shader, source)
      gl!.compileShader(shader)
      if (!gl!.getShaderParameter(shader, gl!.COMPILE_STATUS)) {
        const error = gl!.getShaderInfoLog(shader)
        gl!.deleteShader(shader)
        gl!.deleteProgram(handle)
        throw new Error(`cobe shader: ${error}`)
      }
      gl!.attachShader(handle, shader)
      gl!.deleteShader(shader)
    }
    gl!.linkProgram(handle)
    if (!gl!.getProgramParameter(handle, gl!.LINK_STATUS)) {
      const error = gl!.getProgramInfoLog(handle)
      gl!.deleteProgram(handle)
      throw new Error(`cobe program: ${error}`)
    }
    const names = [
      'uResolution',
      'rotation',
      'roll',
      'globeRadius',
      'ink',
      'green',
      'homepage',
      'dark',
      'waveProgress',
      'waveTarget',
      'pandaConnected',
      'pandaExtent',
      'pixelRatio',
      'mapSamples',
      'uTexture',
    ]
    return {
      handle,
      uniforms: Object.fromEntries(names.map((name) => [name, gl!.getUniformLocation(handle, name)])),
      position: gl!.getAttribLocation(handle, 'aPosition'),
      point: gl!.getAttribLocation(handle, 'aPoint'),
    }
  }
  function uploadTexture() {
    if (destroyed || lost || !image.complete || !image.naturalWidth) return
    gl!.bindTexture(gl!.TEXTURE_2D, texture)
    gl!.texImage2D(gl!.TEXTURE_2D, 0, gl!.RGB, gl!.RGB, gl!.UNSIGNED_BYTE, image)
    if (state) paint()
  }
  function initialize() {
    const extension = gl!.getExtension('ANGLE_instanced_arrays')
    if (!extension) throw new Error('cobe: instanced rendering is unavailable')
    instancing = extension
    programs = [program(globeVertex, common + globeFragment), program(common + pandaVertex, pandaFragment)]
    const quad = gl!.createBuffer()!
    const points = gl!.createBuffer()!
    buffers = [quad, points]
    gl!.bindBuffer(gl!.ARRAY_BUFFER, quad)
    gl!.bufferData(gl!.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl!.STATIC_DRAW)
    gl!.bindBuffer(gl!.ARRAY_BUFFER, points)
    // cobe's longitude basis is a quarter-turn from the original product geometry.
    gl!.bufferData(gl!.ARRAY_BUFFER, new Float32Array(panda.flatMap((p) => [p.z, p.y, -p.x])), gl!.STATIC_DRAW)
    texture = gl!.createTexture()!
    gl!.bindTexture(gl!.TEXTURE_2D, texture)
    gl!.texImage2D(gl!.TEXTURE_2D, 0, gl!.RGB, 1, 1, 0, gl!.RGB, gl!.UNSIGNED_BYTE, new Uint8Array([0, 0, 0]))
    gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_MIN_FILTER, gl!.NEAREST)
    gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_MAG_FILTER, gl!.NEAREST)
    gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_WRAP_S, gl!.REPEAT)
    gl!.texParameteri(gl!.TEXTURE_2D, gl!.TEXTURE_WRAP_T, gl!.REPEAT)
    uploadTexture()
  }
  function paint() {
    if (!state || lost || destroyed || canvas.width === 0 || canvas.height === 0) return
    const s = state
    gl!.viewport(0, 0, canvas.width, canvas.height)
    gl!.clearColor(0, 0, 0, 0)
    gl!.clear(gl!.COLOR_BUFFER_BIT)
    gl!.enable(gl!.BLEND)
    gl!.blendFuncSeparate(gl!.SRC_ALPHA, gl!.ONE_MINUS_SRC_ALPHA, gl!.ONE, gl!.ONE_MINUS_SRC_ALPHA)
    gl!.activeTexture(gl!.TEXTURE0)
    gl!.bindTexture(gl!.TEXTURE_2D, texture)
    for (const p of programs) {
      gl!.useProgram(p.handle)
      const u = p.uniforms
      gl!.uniform2f(u.uResolution!, canvas.width, canvas.height)
      gl!.uniform2f(u.rotation!, s.rotation - Math.PI / 2, s.tilt)
      gl!.uniform1f(u.roll!, Math.PI / 12)
      gl!.uniform1f(u.globeRadius!, s.radius)
      gl!.uniform3fv(u.ink!, s.ink)
      gl!.uniform3fv(u.green!, s.green)
      gl!.uniform1f(u.homepage!, s.homepage ? 1 : 0)
      gl!.uniform1f(u.dark!, s.dark ? 1 : 0)
      gl!.uniform1f(u.waveProgress!, s.waveProgress ?? -1)
      const w = s.waveTarget
      gl!.uniform3f(u.waveTarget!, w?.z ?? 0, w?.y ?? 0, w ? -w.x : 0)
      gl!.uniform1f(u.pandaConnected!, s.pandaConnected ? 1 : 0)
      gl!.uniform1f(u.pandaExtent!, extent)
      gl!.uniform1f(u.pixelRatio!, canvas.width / 600)
      gl!.uniform1f(u.mapSamples!, 10000)
      gl!.uniform1i(u.uTexture!, 0)
      gl!.bindBuffer(gl!.ARRAY_BUFFER, buffers[0]!)
      gl!.enableVertexAttribArray(p.position)
      gl!.vertexAttribPointer(p.position, 2, gl!.FLOAT, false, 0, 0)
      instancing.vertexAttribDivisorANGLE(p.position, 0)
      if (p.point >= 0) {
        gl!.bindBuffer(gl!.ARRAY_BUFFER, buffers[1]!)
        gl!.enableVertexAttribArray(p.point)
        gl!.vertexAttribPointer(p.point, 3, gl!.FLOAT, false, 0, 0)
        instancing.vertexAttribDivisorANGLE(p.point, 1)
        instancing.drawArraysInstancedANGLE(gl!.TRIANGLES, 0, 6, panda.length)
        instancing.vertexAttribDivisorANGLE(p.point, 0)
        gl!.disableVertexAttribArray(p.point)
      } else gl!.drawArrays(gl!.TRIANGLES, 0, 6)
    }
    paintCount++
  }
  function contextLost(event: Event) {
    event.preventDefault()
    lost = true
  }
  function contextRestored() {
    lost = false
    initialize()
    if (state) paint()
  }
  canvas.addEventListener('webglcontextlost', contextLost)
  canvas.addEventListener('webglcontextrestored', contextRestored)
  initialize()
  image.onload = uploadTexture
  image.onerror = () => {
    if (!destroyed) throw new Error('cobe: map texture failed to load')
  }
  image.src = textureURL
  return {
    get paintCount() {
      return paintCount
    },
    update(next: CobeState) {
      state = next
      paint()
    },
    resize(size: number, devicePixelRatio: number) {
      const ratio = devicePixelRatio
      const pixels = Math.max(1, Math.round(size * ratio))
      if (canvas.width !== pixels || canvas.height !== pixels) {
        canvas.width = canvas.height = pixels
      }
    },
    destroy() {
      destroyed = true
      image.onload = image.onerror = null
      canvas.removeEventListener('webglcontextlost', contextLost)
      canvas.removeEventListener('webglcontextrestored', contextRestored)
      programs.forEach((p) => gl!.deleteProgram(p.handle))
      buffers.forEach((b) => gl!.deleteBuffer(b))
      gl!.deleteTexture(texture)
    },
  }
}
